// src/pages/user/UserLearn.tsx
// Routes: /user/learn            — pick a child (or straight in when there is one)
//         /user/learn/:childId   — the child's journey: one "Continue" into the
//                                  next lesson, feedback to read, earlier weeks
//
// Online learning is self-paced inside each week: the child watches short
// lessons, does the activity and hands in evidence. The weekly target says
// how much ground must be covered by the end of the week.

import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Award,
  Blocks,
  CalendarClock,
  Gift,
  Check,
  ChevronRight,
  Hand,
  MessageSquareText,
  Play,
  Settings2,
} from "lucide-react";
import { selectRole } from "../../app/features/authSlice";
import { startChildMode, useChildMode } from "../../app/childMode";
import {
  useGetFeedbackQuery,
  useGetJourneyQuery,
  useGetLearnersQuery,
  useStartTrialMutation,
  errorText,
} from "../../app/api/learningApi";
import type { LearnerChild, WeekProgress } from "../../app/api/learningApi";

const TRACK_LABEL: Record<string, string> = {
  foundation_builders: "Foundation Builders · Ages 6–9",
  explorers: "Explorers · Ages 9–12",
  innovators: "Innovators · Ages 13–18",
};

export function fmtDay(iso?: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
}

export function ProgressBar({ p, light = false }: { p: WeekProgress; light?: boolean }) {
  const pct = p.total ? Math.round((p.submitted / p.total) * 100) : 0;
  const acc = p.total ? Math.round((p.accepted / p.total) * 100) : 0;
  return (
    <div>
      <div className={`relative h-2.5 overflow-hidden rounded-full ${light ? "bg-white/20" : "bg-[#e8ecf5]"}`}>
        <div className={`absolute inset-y-0 left-0 rounded-full ${light ? "bg-white/55" : "bg-[#c9cfe0]"}`} style={{ width: `${pct}%` }} />
        <div className={`absolute inset-y-0 left-0 rounded-full ${light ? "bg-[#e3c76a]" : "bg-[#2a7a56]"}`} style={{ width: `${acc}%` }} />
      </div>
      <p className={`mt-2 text-[13px] ${light ? "text-white/85" : "text-[#5f6780]"}`}>
        {p.submitted} of {p.total} lessons handed in · {p.accepted} accepted
        {p.needsRevision > 0 && (
          <span className={`font-semibold ${light ? "text-[#ffd9a8]" : "text-[#b26a1d]"}`}> · {p.needsRevision} to try again</span>
        )}
      </p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-3xl space-y-5">{children}</div>;
}

function Loading() {
  return (
    <Shell>
      <div className="h-56 animate-pulse rounded-[28px] bg-[#ebeff7]" />
      <div className="h-20 animate-pulse rounded-[22px] bg-[#ebeff7]" />
    </Shell>
  );
}

/* ─── /user/learn ─────────────────────────────────────────────── */

/** The free first week, offered to a child who isn't enrolled yet. */
function FreeWeekCard({ k }: { k: LearnerChild }) {
  const navigate = useNavigate();
  const [start, { isLoading }] = useStartTrialMutation();
  const [msg, setMsg] = useState<string | null>(null);
  const t = k.trial;
  return (
    <div className="rounded-[24px] bg-[#f9fafc] p-5 ring-1 ring-[#e5e8ef]">
      <p className="text-lg font-bold text-[#16213e]">{k.firstName}</p>
      <p className="text-[13px] text-[#8189a0]">{TRACK_LABEL[k.track] ?? k.track}</p>
      {t?.eligible ? (
        <>
          <p className="mt-3 text-sm leading-6 text-[#3b4560]">
            <b>Try the first week free.</b> {k.firstName} gets all of Week 1's lessons for 7 days, with tutor feedback. No payment needed.
          </p>
          <button
            type="button"
            disabled={isLoading}
            onClick={async () => {
              setMsg(null);
              try {
                await start(k.id).unwrap();
                navigate(`/user/learn/${k.id}`);
              } catch (err) {
                setMsg(errorText(err, "Could not start the free week"));
              }
            }}
            className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-[#1b2d5e] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#24397a] disabled:opacity-50"
          >
            <Gift className="h-4 w-4" /> {isLoading ? "Starting…" : "Start the free week"}
          </button>
          {msg && <p className="mt-2 text-sm font-semibold text-[#b94a3a]">{msg}</p>}
          <Link to="/user/enrolments" className="mt-3 block text-[13px] font-semibold text-[#5f6780] hover:text-[#1b2d5e]">
            Or enrol now
          </Link>
        </>
      ) : (
        <>
          <p className="mt-3 text-sm text-[#5f6780]">
            {t?.ended ? `${k.firstName}'s free week has ended. Enrol to carry on from Week 2.` : `Lessons open once ${k.firstName} has an active enrolment.`}
          </p>
          <Link to="/user/enrolments" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1b2d5e]">
            Enrol <ArrowRight className="h-4 w-4" />
          </Link>
        </>
      )}
    </div>
  );
}

export function LearnHome() {
  const { data, isLoading, error } = useGetLearnersQuery();
  if (isLoading) return <Loading />;
  if (error) return <Shell><p className="text-sm text-[#b94a3a]">{errorText(error, "Could not load")}</p></Shell>;
  const kids = data?.data ?? [];
  const active = kids.filter((k) => k.active);
  if (active.length === 1 && kids.length === 1) return <Navigate to={`/user/learn/${active[0].id}`} replace />;

  return (
    <Shell>
      <div>
        <h1 className="text-[1.75rem] font-extrabold tracking-[-0.03em] text-[#16213e]">Who's learning?</h1>
        <p className="mt-1 text-[15px] text-[#5f6780]">Pick a child to open their lessons.</p>
      </div>
      {kids.length === 0 && (
        <div className="rounded-[24px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] p-8 text-center text-sm text-[#5f6780]">
          No children yet. <Link to="/user/children" className="font-semibold text-[#1b2d5e]">Add a child</Link> to get started.
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {kids.map((k) =>
          k.active ? (
            <Link
              key={k.id}
              to={`/user/learn/${k.id}`}
              className="group rounded-[24px] bg-white p-5 ring-1 ring-[#e5e8ef] transition hover:shadow-[0_12px_32px_rgba(27,45,94,0.10)]"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-lg font-bold text-[#16213e]">{k.firstName}</p>
                {k.trial?.active && k.trial.endsAt && (
                  <span className="rounded-full bg-[#edfaf4] px-2.5 py-0.5 text-[12.5px] font-bold text-[#2a7a56]">
                    Free week · until {fmtDay(k.trial.endsAt)}
                  </span>
                )}
                {!!k.unseenFeedback && (
                  <span className="rounded-full bg-[#fbf5e1] px-2.5 py-0.5 text-[12.5px] font-bold text-[#8a6d12]">
                    {k.unseenFeedback} new feedback
                  </span>
                )}
              </div>
              <p className="text-[13px] text-[#8189a0]">{TRACK_LABEL[k.track] ?? k.track}</p>
              {k.currentWeek && (
                <p className="mt-3 text-[15px] font-semibold text-[#3b4560]">
                  Week {k.calendarWeek}: {k.currentWeek.title}
                </p>
              )}
              {k.progress && k.progress.total > 0 && <div className="mt-3"><ProgressBar p={k.progress} /></div>}
              <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#1b2d5e]">
                Open lessons <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ) : (
            <FreeWeekCard key={k.id} k={k} />
          ),
        )}
      </div>
    </Shell>
  );
}

/* ─── /user/learn/:childId ────────────────────────────────────── */

export function LearnJourney() {
  const { childId = "" } = useParams();
  const roleRaw = useSelector(selectRole);
  const childMode = useChildMode();
  // In child mode a parent's device behaves like the child's own view
  const role = roleRaw === "parent" && childMode ? "child" : roleRaw;
  const isParent = role === "parent";
  const { data, isLoading, error } = useGetJourneyQuery(childId);
  const { data: fb } = useGetFeedbackQuery(childId, { skip: !childId });

  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <Shell>
        <div className="rounded-[24px] border border-[#f5d9a0] bg-[#fff8ee] p-6 text-sm text-[#7a4a10]">
          {errorText(error, "Could not load the lessons")}
        </div>
      </Shell>
    );

  const j = data.data;
  const [current, ...past] = j.weeks;
  const unseen = (fb?.data ?? []).filter((f) => f.unseen);
  const p = current.progress;
  const cta = p.submitted === 0 ? "Start this week" : p.allSubmitted ? "Look back at this week" : "Continue";

  return (
    <Shell>
      {isParent && (
        <Link to="/user/learn" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5f6780] hover:text-[#16213e]">
          <ArrowLeft className="h-4 w-4" /> All children
        </Link>
      )}

      <div>
        <h1 className="text-[1.75rem] font-extrabold tracking-[-0.03em] text-[#16213e]">
          {isParent ? `${j.child.firstName}'s lessons` : `Hi ${j.child.firstName}!`}
        </h1>
        <p className="mt-0.5 text-[15px] text-[#5f6780]">{TRACK_LABEL[j.child.track] ?? j.child.track}</p>
      </div>

      {j.trial?.active && (
        <div className="flex flex-wrap items-center gap-3 rounded-[22px] border border-[#b6e8d0] bg-[#edfaf4] px-5 py-4">
          <Gift className="h-6 w-6 flex-shrink-0 text-[#2a7a56]" />
          <p className="min-w-0 flex-1 text-[14px] leading-6 text-[#1f5c3f]">
            <b>Free week:</b> Week 1 is open until {fmtDay(j.trial.endsAt)}. {isParent ? `Enrol ${j.child.firstName}` : "Enrol"} any time to carry on with Week 2.
          </p>
          {isParent && (
            <Link to="/user/enrolments" className="rounded-xl bg-[#1b2d5e] px-4 py-2 text-sm font-bold text-white hover:bg-[#24397a]">
              Enrol
            </Link>
          )}
        </div>
      )}

      {/* The one thing to do: carry on */}
      <section className="relative overflow-hidden rounded-[28px] bg-[#1b2d5e] p-6 text-white shadow-[0_20px_50px_rgba(27,45,94,0.30)] sm:p-8">
        <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#c9a227]/20 blur-2xl" />
        <p className="relative text-[12.5px] font-bold uppercase tracking-[0.14em] text-[#e3c76a]">
          Week {j.calendarWeek} · ends {fmtDay(j.weekEndsAt)}
        </p>
        <h2 className="relative mt-2 text-2xl font-extrabold leading-tight tracking-[-0.02em] sm:text-[1.9rem]">{current.title}</h2>
        {current.topics && <p className="relative mt-1.5 text-[15px] text-white/75">{current.topics}</p>}
        {current.lessonsReady ? (
          <>
            <div className="relative mt-5"><ProgressBar p={p} light /></div>
            <Link
              to={`/user/learn/${j.child.id}/${current.key}${p.allSubmitted ? "" : "?next=1"}`}
              className="relative mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 text-base font-bold text-[#1b2d5e] transition hover:bg-[#fbf5e1] sm:w-auto"
            >
              <Play className="h-5 w-5 fill-[#1b2d5e]" /> {cta}
            </Link>
          </>
        ) : (
          <p className="relative mt-5 rounded-2xl bg-white/10 p-4 text-[15px]">
            This week's lessons are being prepared. Catch up on earlier weeks below.
          </p>
        )}
      </section>

      {unseen.length > 0 && (
        <Link
          to={`/user/learn/${j.child.id}/${unseen[0].weekKey}/${unseen[0].lessonKey}`}
          className="flex items-center gap-4 rounded-[22px] border border-[#e3c76a] bg-[#fbf5e1] p-4 transition hover:shadow-[0_8px_24px_rgba(201,162,39,0.18)]"
        >
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-white">
            <MessageSquareText className="h-5 w-5 text-[#8a6d12]" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-bold text-[#16213e]">
              New feedback on {unseen.length} lesson{unseen.length > 1 ? "s" : ""}
            </span>
            <span className="block truncate text-[13px] text-[#5f6780]">Start with "{unseen[0].lessonTitle}"</span>
          </span>
          <ChevronRight className="h-5 w-5 flex-shrink-0 text-[#8a6d12]" />
        </Link>
      )}

      {j.weeksBehind > 0 && (
        <p className="flex gap-2.5 rounded-[18px] bg-[#fff8ee] px-4 py-3 text-sm leading-6 text-[#7a4a10]">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {j.weeksBehind} earlier week{j.weeksBehind > 1 ? "s have" : " has"} work still to hand in. Finishing them keeps{" "}
          {isParent ? j.child.firstName : "you"} on track for the showcase.
        </p>
      )}

      {past.length > 0 && (
        <details className="group rounded-[22px] bg-white ring-1 ring-[#e5e8ef]" open={j.weeksBehind > 0}>
          <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 text-[15px] font-bold text-[#16213e]">
            Earlier weeks ({past.length})
            <ChevronRight className="h-5 w-5 text-[#8189a0] transition group-open:rotate-90" />
          </summary>
          <div className="divide-y divide-[#ebeff7] border-t border-[#ebeff7]">
            {past.map((w) => (
              <Link key={w.key} to={`/user/learn/${j.child.id}/${w.key}`} className="flex items-center gap-4 px-5 py-3 hover:bg-[#f9fafc]">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#ebeff7] text-xs font-bold text-[#1b2d5e]">
                  {w.calendarWeek}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-[#16213e]">{w.title}</span>
                  <span className="block text-[12.5px] text-[#8189a0]">
                    {w.lessonsReady ? `${w.progress.submitted}/${w.progress.total} handed in · ${w.progress.accepted} accepted` : "Lessons being prepared"}
                  </span>
                </span>
                {w.progress.targetMet ? (
                  <span className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#2a7a56]">
                    <Check className="h-4 w-4" /> Done
                  </span>
                ) : w.lessonsReady && !w.progress.allSubmitted ? (
                  <span className="text-[12.5px] font-bold text-[#b26a1d]">Unfinished</span>
                ) : null}
              </Link>
            ))}
          </div>
        </details>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Link
          to={`/user/learn/${j.child.id}/showcase`}
          className="flex items-center gap-3 rounded-[22px] bg-white p-4 ring-1 ring-[#e5e8ef] transition hover:shadow-[0_8px_24px_rgba(27,45,94,0.08)]"
        >
          <Award className="h-6 w-6 flex-shrink-0 text-[#c9a227]" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-[#16213e]">Showcase &amp; gallery</span>
            <span className="block text-[12.5px] text-[#5f6780]">Results, awards and certificates</span>
          </span>
          <ChevronRight className="h-5 w-5 text-[#a6adbf]" />
        </Link>
        {/* The in-app Scratch and code projects */}
        <Link
          to={`/user/learn/${j.child.id}/projects`}
          className="flex items-center gap-3 rounded-[22px] bg-white p-4 ring-1 ring-[#e5e8ef] transition hover:shadow-[0_8px_24px_rgba(27,45,94,0.08)]"
        >
          <Blocks className="h-6 w-6 flex-shrink-0 text-[#1b2d5e]" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-[#16213e]">{isParent ? "Projects" : "My projects"}</span>
            <span className="block text-[12.5px] text-[#5f6780]">
              {j.child.track === "foundation_builders"
                ? "Scratch games and stories, and web pages"
                : j.child.track === "innovators"
                  ? "Websites, React and React Native apps"
                  : "Your websites and code"}
            </span>
          </span>
          <ChevronRight className="h-5 w-5 text-[#a6adbf]" />
        </Link>
        {j.nextWeek && (
          <div className="flex items-center gap-3 rounded-[22px] bg-white/60 p-4 ring-1 ring-[#e5e8ef]">
            <CalendarClock className="h-6 w-6 flex-shrink-0 text-[#5f6780]" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold text-[#16213e]">Next: {j.nextWeek.title}</span>
              <span className="block text-[12.5px] text-[#5f6780]">Opens {fmtDay(j.nextWeek.opensAt)}</span>
            </span>
          </div>
        )}
      </div>

      {isParent && (
        <div className="flex flex-wrap items-center gap-2 border-t border-[#e5e8ef] pt-4">
          <span className="mr-1 text-[13px] font-semibold text-[#8189a0]">Grown-up tools</span>
          <button
            type="button"
            onClick={() => startChildMode({ childId: j.child.id, firstName: j.child.firstName })}
            className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[13px] font-semibold text-[#1b2d5e] ring-1 ring-[#dde2ec] hover:bg-[#f1f3f8]"
            title={`Shows only ${j.child.firstName}'s lessons, in bigger text. A grown-up question is needed to leave.`}
          >
            <Hand className="h-4 w-4" /> Hand the device to {j.child.firstName}
          </button>
          <Link
            to={`/user/children/${j.child.id}#learning`}
            className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[13px] font-semibold text-[#1b2d5e] ring-1 ring-[#dde2ec] hover:bg-[#f1f3f8]"
          >
            <Settings2 className="h-4 w-4" /> Sign-in and AI key
          </Link>
        </div>
      )}
    </Shell>
  );
}
